import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import * as grid from 'view/styles/grid';
import type { Option, ValueType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import type { FieldFormProps, NestedFieldValueType } from '@atlassian/help-center-common-component/fields/types';
import { ThemedSelect } from '@atlassian/help-center-common-component/themed-select';
import { toSingleSelectValue } from '@atlassian/help-center-common-util/select';

export interface NestedOption extends Option<string> {
    children: Option<string>[];
}

interface Props extends FieldFormProps<NestedFieldValueType> {
    actionSubjectId: string;
    options: NestedOption[];
    defaultValue?: NestedFieldValueType;
    value?: NestedFieldValueType;
    isClearable?: boolean;
    selectWidth?: number;
}

interface State {
    groupValue?: string;
    childValue?: string;
}

class ThemedSelectGroup extends React.PureComponent<Props, State> {
    state: State = {};

    constructor(props: Props) {
        super(props);

        const { defaultValue, options } = this.props;

        if (defaultValue) {
            const defaultGroup = options.find((group) => group.value === defaultValue.primaryValue);
            const defaultChild = defaultGroup && defaultGroup.children.find((g) => g.value === defaultValue.children);
            this.state = {
                groupValue: defaultGroup && defaultGroup.value,
                childValue: defaultChild && defaultChild.value,
            };
        }
    }

    getGroupStateValue() {
        const { value } = this.props;
        return value === undefined ? this.state.groupValue : value.primaryValue;
    }

    getChildStateValue() {
        const { value } = this.props;
        return value === undefined ? this.state.childValue : value.children;
    }

    getSelectedGroup() {
        const { options } = this.props;
        const groupValue = this.getGroupStateValue();
        // We make sure to set null instead of undefined here to ensure value is updated correctly;
        return (groupValue && options.find((group) => group.value === groupValue)) || null;
    }

    getSelectedChild(selectedGroup: NestedOption | null) {
        const childValue = this.getChildStateValue();
        // We make sure to set null instead of undefined here to ensure value is updated correctly;
        return (selectedGroup && selectedGroup.children.find((g) => g.value === childValue)) || null;
    }

    onGroupSelected = (groupSelected: ValueType<Option<string>>) => {
        const groupValue = toSingleSelectValue(groupSelected)?.value;
        this.setState({
            groupValue,
            childValue: undefined,
        });
        this.props.onChange?.(this.props.id, {
            primaryValue: groupValue ?? null,
            children: undefined,
        });
    };

    onChildSelected = (childSelected: ValueType<Option<string>>) => {
        const groupValue = this.getGroupStateValue();
        const childValue = toSingleSelectValue(childSelected)?.value;
        if (groupValue) {
            this.setState({
                childValue,
            });
            this.props.onChange?.(this.props.id, {
                primaryValue: groupValue,
                children: childValue ?? null,
            });
        }
    };

    render() {
        const {
            actionSubjectId,
            label,
            required,
            errorMessage,
            description,
            id,
            options,
            disabled,
            isClearable,
            selectWidth,
        } = this.props;
        const selectedGroup = this.getSelectedGroup();
        const selectedChild = this.getSelectedChild(selectedGroup);
        const validationState = !!errorMessage ? 'error' : 'default';
        const clearable = isClearable === undefined ? true : isClearable;

        return (
            <FieldBase
                htmlFor={id}
                label={label}
                required={required}
                errorMessage={errorMessage}
                description={description}
            >
                <Container>
                    <SelectContainer
                        marginRight={token('space.100', '8px')}
                        selectWidth={selectWidth}
                        data-test-id={`select-container-${id}`}
                        aria-describedby={`${id}-field-description`}
                    >
                        <ThemedSelect
                            actionSubjectId={`${actionSubjectId}PrimarySelect`}
                            inputId={id}
                            options={options}
                            value={selectedGroup}
                            onChange={this.onGroupSelected}
                            validationState={validationState}
                            isDisabled={disabled}
                            isClearable={clearable}
                        />
                    </SelectContainer>
                    <SelectContainer
                        selectWidth={selectWidth}
                        data-test-id={`select-container-${id}:1`}
                        aria-describedby={`${id}-field-description`}
                    >
                        <ThemedSelect
                            actionSubjectId={`${actionSubjectId}SecondarySelect`}
                            inputId={`${id}:1`}
                            options={selectedGroup ? selectedGroup.children : []}
                            value={selectedChild}
                            onChange={this.onChildSelected}
                            validationState={validationState}
                            isDisabled={disabled}
                            isClearable={clearable}
                        />
                    </SelectContainer>
                </Container>
            </FieldBase>
        );
    }
}

export default ThemedSelectGroup;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-top: ${token('space.negative.050', '-4px')};
`;

interface SelectContainerProps {
    marginRight?: string;
    selectWidth?: number;
}

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const SelectContainer = styled.div`
    /* stylelint-disable scale-unlimited/declaration-strict-value */
    width: ${(props: SelectContainerProps) =>
        `${props && props.selectWidth ? `${props.selectWidth}px` : `calc(50% - ${grid.multiple(0.5).px})`}`};
    margin-top: ${token('space.050', '4px')};
    margin-right: ${(props: SelectContainerProps) => `${props && props.marginRight ? props.marginRight : '0px'}`};
`;
